import type {
  IAcquirerOnboardingFlag,
  IOnboardingFlag,
} from "../../../../interfaces/businessFull.interface";

export type FlagPersonRef = {
  _id?: string;
  firstName?: string;
  lastName?: string;
  adyenLegalEntityId?: string;
};

type PersonFlagGroup = {
  kind: "person";
  personId: string;
  name: string;
  flags: IAcquirerOnboardingFlag[];
};

export type OnboardingFlagGroup =
  { kind: "business"; flags: IAcquirerOnboardingFlag[] } | PersonFlagGroup;

const ONBOARDING_FLAG_TYPE_LABELS: Record<string, string> = {
  PERSON_ADDRESS_DATA_MISSING: "Residential address missing",
};

export function getOnboardingFlagTypeLabel(type: string): string {
  return ONBOARDING_FLAG_TYPE_LABELS[type] ?? type;
}

function flagIdentityKey(flag: IAcquirerOnboardingFlag): string {
  return `${flag.type}|${flag.message ?? ""}|${flag.personId ?? ""}|${
    flag.adyenLegalEntityId ?? ""
  }`;
}

export function mergeOnboardingFlags(
  acquirerFlags: IAcquirerOnboardingFlag[],
  legacyFlags: Array<
    Pick<
      IOnboardingFlag,
      "type" | "message" | "personId" | "adyenLegalEntityId"
    >
  >
): IAcquirerOnboardingFlag[] {
  const seen = new Set<string>();
  const merged: IAcquirerOnboardingFlag[] = [];

  const push = (flag: IAcquirerOnboardingFlag): void => {
    const key = flagIdentityKey(flag);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(flag);
  };

  for (const flag of acquirerFlags) push(flag);
  for (const flag of legacyFlags) {
    push({
      type: flag.type,
      message: flag.message,
      personId: flag.personId,
      adyenLegalEntityId: flag.adyenLegalEntityId,
    });
  }

  return merged;
}

function personDisplayName(person: FlagPersonRef): string {
  return [person.firstName, person.lastName]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

function resolveFlagOwner(
  flag: IAcquirerOnboardingFlag,
  persons: FlagPersonRef[]
): FlagPersonRef | undefined {
  if (flag.personId) {
    const byId = persons.find((person) => person._id === flag.personId);
    if (byId) return personDisplayName(byId) ? byId : undefined;
  }

  if (!flag.adyenLegalEntityId) return undefined;

  const matches = persons.filter(
    (person) => person.adyenLegalEntityId === flag.adyenLegalEntityId
  );
  if (matches.length !== 1) return undefined;
  return personDisplayName(matches[0]) ? matches[0] : undefined;
}

export function groupOnboardingFlagsByPerson(
  flags: IAcquirerOnboardingFlag[],
  persons: FlagPersonRef[]
): { mode: "flat" | "grouped"; groups: OnboardingFlagGroup[] } {
  const personGroups = new Map<string, PersonFlagGroup>();
  const personOrder: string[] = [];
  const businessFlags: IAcquirerOnboardingFlag[] = [];

  for (const flag of flags) {
    const owner = resolveFlagOwner(flag, persons);
    const groupKey = owner?._id ?? owner?.adyenLegalEntityId;
    if (!owner || !groupKey) {
      businessFlags.push(flag);
      continue;
    }

    const existing = personGroups.get(groupKey);
    if (existing) {
      existing.flags.push(flag);
      continue;
    }

    personGroups.set(groupKey, {
      kind: "person",
      personId: groupKey,
      name: personDisplayName(owner),
      flags: [flag],
    });
    personOrder.push(groupKey);
  }

  const groups: OnboardingFlagGroup[] = personOrder.map((key) =>
    personGroups.get(key)!
  );
  if (businessFlags.length > 0) {
    groups.push({ kind: "business", flags: businessFlags });
  }

  return {
    mode: personGroups.size > 0 ? "grouped" : "flat",
    groups,
  };
}
